/**
 * Tetris game logic hook.
 * Implements a standard Tetris ruleset using useReducer + setInterval.
 * All game state (board, piece, score) is derived from the reducer; no mutable globals.
 */

import { useReducer, useRef, useEffect, useCallback } from 'react';
import { TETRIS_CONFIG } from '../constants/TetrisConfig';

const { boardWidth: W, boardHeight: H, initialDropInterval, intervalDecrement, minDropInterval, linesPerLevel } =
  TETRIS_CONFIG;

// ── Tetromino definitions ──────────────────────────────────────────────────────

const TETROMINOES = [
  { shape: [[0,0,0,0],[1,1,1,1],[0,0,0,0],[0,0,0,0]], color: '#00cfcf' }, // I
  { shape: [[1,1],[1,1]], color: '#cfcf00' },                               // O
  { shape: [[0,1,0],[1,1,1],[0,0,0]], color: '#9f00cf' },                   // T
  { shape: [[0,1,1],[1,1,0],[0,0,0]], color: '#00cf00' },                   // S
  { shape: [[1,1,0],[0,1,1],[0,0,0]], color: '#cf0000' },                   // Z
  { shape: [[1,0,0],[1,1,1],[0,0,0]], color: '#0060cf' },                   // J
  { shape: [[0,0,1],[1,1,1],[0,0,0]], color: '#cf7000' },                   // L
] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

type Cell = string | null;
type Board = Cell[][];

interface ActivePiece {
  shape: number[][];
  color: string;
  x: number;
  y: number;
}

export interface NextPiece {
  shape: number[][];
  color: string;
}

export type GameStatus = 'idle' | 'playing' | 'paused' | 'gameover';

interface GameState {
  board: Board;
  current: ActivePiece | null;
  next: NextPiece;
  score: number;
  lines: number;
  level: number;
  status: GameStatus;
  maxScore: number;
}

type Action =
  | { type: 'START' }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'MOVE'; dx: number }
  | { type: 'SOFT_DROP' }
  | { type: 'ROTATE' }
  | { type: 'HARD_DROP' }
  | { type: 'TICK' }
  | { type: 'SET_MAX_SCORE'; value: number };

// ── Pure helpers ──────────────────────────────────────────────────────────────

function emptyBoard(): Board {
  return Array.from({ length: H }, () => Array<Cell>(W).fill(null));
}

function randomPiece(): NextPiece {
  const p = TETROMINOES[Math.floor(Math.random() * TETROMINOES.length)];
  return { shape: p.shape.map((r) => [...r]), color: p.color };
}

function spawnPiece(next: NextPiece): ActivePiece {
  return {
    ...next,
    shape: next.shape.map((r) => [...r]),
    x: Math.floor((W - next.shape[0].length) / 2),
    y: 0,
  };
}

/** True if `shape` at (x, y) fits inside the board with no occupied cells. */
function isValid(board: Board, shape: number[][], x: number, y: number): boolean {
  for (let r = 0; r < shape.length; r++) {
    for (let c = 0; c < shape[r].length; c++) {
      if (!shape[r][c]) continue;
      const nr = y + r;
      const nc = x + c;
      if (nr >= H || nc < 0 || nc >= W) return false;
      if (nr >= 0 && board[nr][nc] !== null) return false;
    }
  }
  return true;
}

/** Rotate shape 90° clockwise. */
function rotateCW(shape: number[][]): number[][] {
  const size = shape.length;
  return Array.from({ length: size }, (_, c) =>
    Array.from({ length: size }, (__, r) => shape[size - 1 - r][c]),
  );
}

/** Lock the piece into the board and clear any full rows. */
function lockAndClear(
  board: Board,
  piece: ActivePiece,
): { newBoard: Board; linesCleared: number } {
  const newBoard = board.map((row) => [...row]);
  for (let r = 0; r < piece.shape.length; r++) {
    for (let c = 0; c < piece.shape[r].length; c++) {
      if (!piece.shape[r][c]) continue;
      const nr = piece.y + r;
      const nc = piece.x + c;
      if (nr >= 0 && nr < H) newBoard[nr][nc] = piece.color;
    }
  }
  const cleared = newBoard.filter((row) => row.every((cell) => cell !== null));
  const remaining = newBoard.filter((row) => !row.every((cell) => cell !== null));
  const linesCleared = cleared.length;
  const emptyRows = Array.from({ length: linesCleared }, () => Array<Cell>(W).fill(null));
  return { newBoard: [...emptyRows, ...remaining], linesCleared };
}

/** Standard Tetris line-clear scoring multiplied by current level. */
function calcScore(linesCleared: number, level: number): number {
  const table = [0, 100, 300, 500, 800];
  return (table[linesCleared] ?? 0) * level;
}

/** Merge the active piece into the board for rendering. */
function mergeBoard(board: Board, current: ActivePiece | null): Board {
  if (!current) return board;
  const result = board.map((row) => [...row]);
  for (let r = 0; r < current.shape.length; r++) {
    for (let c = 0; c < current.shape[r].length; c++) {
      if (!current.shape[r][c]) continue;
      const nr = current.y + r;
      const nc = current.x + c;
      if (nr >= 0 && nr < H && nc >= 0 && nc < W) {
        result[nr][nc] = current.color;
      }
    }
  }
  return result;
}

// ── Reducer ───────────────────────────────────────────────────────────────────

function reducer(state: GameState, action: Action): GameState {
  switch (action.type) {
    case 'START': {
      const first = randomPiece();
      const next = randomPiece();
      return {
        board: emptyBoard(),
        current: spawnPiece(first),
        next,
        score: 0,
        lines: 0,
        level: 1,
        status: 'playing',
        maxScore: state.maxScore,
      };
    }

    case 'PAUSE':
      return state.status === 'playing' ? { ...state, status: 'paused' } : state;

    case 'RESUME':
      return state.status === 'paused' ? { ...state, status: 'playing' } : state;

    case 'MOVE': {
      if (!state.current || state.status !== 'playing') return state;
      const nx = state.current.x + action.dx;
      if (!isValid(state.board, state.current.shape, nx, state.current.y)) return state;
      return { ...state, current: { ...state.current, x: nx } };
    }

    case 'ROTATE': {
      if (!state.current || state.status !== 'playing') return state;
      const rotated = rotateCW(state.current.shape);
      // Wall-kick: try offsets 0, ±1, ±2
      for (const kick of [0, -1, 1, -2, 2]) {
        const kx = state.current.x + kick;
        if (isValid(state.board, rotated, kx, state.current.y)) {
          return { ...state, current: { ...state.current, shape: rotated, x: kx } };
        }
      }
      return state;
    }

    case 'SOFT_DROP':
    case 'TICK': {
      if (!state.current || state.status !== 'playing') return state;
      const ny = state.current.y + 1;
      if (isValid(state.board, state.current.shape, state.current.x, ny)) {
        return { ...state, current: { ...state.current, y: ny } };
      }
      // Piece can't move down — lock it
      return lockPiece(state);
    }

    case 'HARD_DROP': {
      if (!state.current || state.status !== 'playing') return state;
      let y = state.current.y;
      while (isValid(state.board, state.current.shape, state.current.x, y + 1)) y++;
      const landed = { ...state.current, y };
      return lockPiece({ ...state, current: landed });
    }

    case 'SET_MAX_SCORE':
      return { ...state, maxScore: Math.max(state.maxScore, action.value) };

    default:
      return state;
  }
}

/** Shared lock + line clear + next piece spawn logic. */
function lockPiece(state: GameState): GameState {
  if (!state.current) return state;
  const { newBoard, linesCleared } = lockAndClear(state.board, state.current);
  const newScore = state.score + calcScore(linesCleared, state.level);
  const newLines = state.lines + linesCleared;
  const newLevel = Math.floor(newLines / linesPerLevel) + 1;
  const newMaxScore = Math.max(state.maxScore, newScore);
  const newCurrent = spawnPiece(state.next);
  // Game over: new piece spawns into an occupied cell
  if (!isValid(newBoard, newCurrent.shape, newCurrent.x, newCurrent.y)) {
    return {
      ...state,
      board: newBoard,
      current: null,
      score: newScore,
      lines: newLines,
      level: newLevel,
      status: 'gameover',
      maxScore: newMaxScore,
    };
  }
  return {
    ...state,
    board: newBoard,
    current: newCurrent,
    next: randomPiece(),
    score: newScore,
    lines: newLines,
    level: newLevel,
    maxScore: newMaxScore,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Complete Tetris game state and actions.
 * The board returned already has the active piece merged in for rendering.
 */
export function useTetris() {
  const [state, dispatch] = useReducer(reducer, {
    board: emptyBoard(),
    current: null,
    next: randomPiece(),
    score: 0,
    lines: 0,
    level: 1,
    status: 'idle' as GameStatus,
    maxScore: 0,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Current drop interval derived from level. */
  const dropInterval = Math.max(
    minDropInterval,
    initialDropInterval - (state.level - 1) * intervalDecrement,
  );

  // Auto-drop: restart the interval whenever status or speed changes
  useEffect(() => {
    if (state.status !== 'playing') {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    intervalRef.current = setInterval(() => dispatch({ type: 'TICK' }), dropInterval);
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [state.status, dropInterval]);

  const start = useCallback(() => dispatch({ type: 'START' }), []);
  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const resume = useCallback(() => dispatch({ type: 'RESUME' }), []);
  const moveLeft = useCallback(() => dispatch({ type: 'MOVE', dx: -1 }), []);
  const moveRight = useCallback(() => dispatch({ type: 'MOVE', dx: 1 }), []);
  const softDrop = useCallback(() => dispatch({ type: 'SOFT_DROP' }), []);
  const rotateAction = useCallback(() => dispatch({ type: 'ROTATE' }), []);
  const hardDrop = useCallback(() => dispatch({ type: 'HARD_DROP' }), []);
  /** Initialise maxScore from a persisted value (e.g. fetched from the API at mount). */
  const setMaxScore = useCallback((value: number) => dispatch({ type: 'SET_MAX_SCORE', value }), []);

  return {
    /** 20×10 board with the active piece already painted in (read-only). */
    board: mergeBoard(state.board, state.current),
    next: state.next,
    score: state.score,
    lines: state.lines,
    level: state.level,
    status: state.status,
    maxScore: state.maxScore,
    start,
    pause,
    resume,
    moveLeft,
    moveRight,
    softDrop,
    rotate: rotateAction,
    hardDrop,
    setMaxScore,
  };
}
